import { downloadBusinessReportApi } from "@/api/services/main/business-report";
import { useGetMatchInfo } from "@/api/services/main/match";
import { IconDownloadBlue, IconExpandMore } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Select } from "@/components/elements/Select";
import { TMatchOffice } from "@/features/match/types/MatchForm";
import { button, orange } from "@/theme/colors";
import { TOption } from "@/types/common";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { toast } from "@/utils/toast";
import { DOWNLOAD_SUCCESS_MESSAGE, DOWNLOAD_FAILED_MESSAGE } from "@/constants/common";

/** Current fiscal year (April-March, Japan standard) */
const getCurrentFiscalYear = () => {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
};

/** Generate fiscal year options: 5 past years + current year */
const getFiscalYearOptions = (): TOption[] => {
  const currentFY = getCurrentFiscalYear();
  return Array.from({ length: 5 }, (_, i) => ({
    label: `${currentFY - 4 + i}年度`,
    value: currentFY - 4 + i,
  }));
};

type TOfficeReportItemProps = {
  office: TMatchOffice;
  onDownload: (officeId: number, officeName: string, fiscalYear: number) => Promise<void>;
};

const OfficeReportItem: FC<TOfficeReportItemProps> = ({ office, onDownload }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(getCurrentFiscalYear());
  const yearOptions = useMemo(() => getFiscalYearOptions(), []);

  const handleDownload = async () => {
    if (!office.id || !office.officeName) return;
    setIsLoading(true);
    try {
      await onDownload(office.id, office.officeName, selectedYear);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack direction="row" alignItems="center" py={1.5}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="sub16">{office.officeName}</Typography>
      </Box>
      <Box sx={{ flex: 2, display: "flex", justifyContent: "center" }}>
        <Select
          options={yearOptions}
          value={selectedYear}
          onChange={(value) => setSelectedYear(Number(value))}
          size="md"
          clearable={false}
          nonControl
          IconComponent={IconExpandMore}
          sx={{ width: "100px", height: "33px" }}
        />
        <Button
          sx={{
            marginLeft: "24px",
          }}
          variant="outline"
          size="sm"
          startIcon={<IconDownloadBlue color={button.primary} />}
          onClick={handleDownload}
          isLoading={isLoading}
        >
          <Typography variant="body14Semi">事業報告書(.xlsx)をダウンロード</Typography>
        </Button>
      </Box>
    </Stack>
  );
};

export const OfficeReportList: FC = () => {
  const { data, isLoading } = useGetMatchInfo();
  const matchOffices = data?.data?.matchOffices ?? [];

  const handleDownload = async (officeId: number, officeName: string, fiscalYear: number) => {
    try {
      const response = await downloadBusinessReportApi({ matchOfficeId: officeId, fiscalYear });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${officeName}の事業報告書_${fiscalYear}年度.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(DOWNLOAD_SUCCESS_MESSAGE);
    } catch {
      toast.error(DOWNLOAD_FAILED_MESSAGE);
    }
  };

  if (isLoading) {
    return (
      <InnerBox title={<Typography variant="sub16Bold">事業所一覧</Typography>} showDivider={false}>
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      </InnerBox>
    );
  }

  if (matchOffices.length === 0) {
    return (
      <InnerBox title={<Typography variant="sub16Bold">事業所一覧</Typography>} showDivider={false}>
        <Typography variant="body14" color="text.secondary" py={2}>
          事業所が登録されていません。
        </Typography>
      </InnerBox>
    );
  }

  return (
    <InnerBox title={<Typography variant="sub16Bold">事業所一覧</Typography>} showDivider={false}>
      <Stack gap={1}>
        {matchOffices
          .filter((office) => office.officeName)
          .map((office) => (
            <OfficeReportItem key={office.id} office={office} onDownload={handleDownload} />
          ))}
        <Typography variant="body14" color={orange[80]}>
          ※ この事業報告書に反映される数値は、在留資格が「特定技能」の対象者のみを集計したものです。
          <br /> その他の在留資格は含まれておりません。
          <br /> ※ この事業報告書はドラフトです。ダウンロード後確認して適宜修正してください。
        </Typography>
      </Stack>
    </InnerBox>
  );
};
